import { ArrowLeftIcon, ArrowRightIcon, ExclamationTriangleIcon, PlayIcon, StopIcon, TrashIcon } from '@heroicons/react/20/solid';
import { useRevalidator } from '@remix-run/react';
import { clsx } from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useLocale } from 'remix-i18next/react';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { getMediumExamples, getMediumTitle } from '#common/medium-display';
import { type CoreTheme } from '#speaker-shop/hooks/useCoreTheme.ts';
import { calculateCheapestUnifiedPrice } from '#speaker-shop/models/price-calculation';
import { AudioGeneration } from './AudioGeneration';
import { CoreCheckout } from './CoreCheckout';
import { CurrencySwitcher } from './CurrencySwitcher';
import { PlayButton } from './PlayButton';
import { useRecordingContext } from './RecordingContext';
import { type ResolvedAvailableUse, type UnifiedShopData, type UnifiedVoice } from './shop-layouts/types';

type OrderStep = 'generate' | 'use' | 'review';

interface Props {
    data: UnifiedShopData;
    activeVoice: UnifiedVoice;
    activeVoiceId: string;
    selectedUseId: string | null;
    onActiveVoiceChange: (voiceId: string) => void;
    onSelectedUseChange: (useId: string) => void;
    onApiRequest: () => void;
    theme: CoreTheme;
}

const voiceColors = [
    'bg-[#0d5c8a] text-white',
    'bg-[#1479ad] text-white',
    'bg-[#1e9cd7] text-white',
    'bg-[#4fb3e8] text-[#0d2233]',
    'bg-[#8ad1f2] text-[#0d2233]',
];

function getVoiceColor(voiceId: string) {
    const hash = [ ...voiceId ].reduce((value, character) => ((value * 31) + character.charCodeAt(0)) >>> 0, 0);
    return voiceColors[hash % voiceColors.length]!;
}

export function CoreOrderFlow({
    data,
    activeVoice,
    activeVoiceId,
    selectedUseId,
    onActiveVoiceChange,
    onSelectedUseChange,
    onApiRequest,
    theme,
}: Props) {
    const { t, rawT } = useEditableTranslation('core-shop');
    const locale = useLocale();
    const revalidator = useRevalidator();
    const refreshData = useCallback(() => revalidator.revalidate(), [ revalidator ]);
    const { recordings, deleteTake, setCheckState } = useRecordingContext();
    const [ step, setStep ] = useState<OrderStep>('generate');

    const allTakes = useMemo(() => recordings.flatMap(recording => recording.takes), [ recordings ]);
    const selectedTakes = useMemo(() => allTakes.filter(take => take.isChecked), [ allTakes ]);
    const selectedUse = data.availableUses.find(use => use.id === selectedUseId) ?? null;
    const hasUseStep = data.availableUses.length > 1;
    const steps: OrderStep[] = hasUseStep ? [ 'generate', 'use', 'review' ] : [ 'generate', 'review' ];
    const currentStepIndex = steps.indexOf(step);
    const selectedRecordings = useMemo(
        () => recordings.map(recording => ({ takes: recording.takes.filter(take => take.isChecked) })),
        [ recordings ]
    );
    const selectedPrice = calculateCheapestUnifiedPrice(selectedRecordings, data.availableUses);
    const totalCharacters = selectedTakes.reduce((sum, take) => sum + take.text.length, 0);

    const goToStep = (nextStep: OrderStep) => {
        setStep(nextStep);
        requestAnimationFrame(() => {
            document.getElementById('cvx-order-flow')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };

    return (
        <section className='core-order-band' id='cvx-order-flow'>
            <div className='core-page py-9'>
                <div className='core-section-label'>{t('order.label')}</div>
                <p className='mb-5 max-w-2xl text-sm leading-6 opacity-65'>{t('order.intro')}</p>

                <div className='mb-5 flex items-center gap-3' aria-label={rawT('order.progress-label')}>
                    <div className='flex gap-1.5' aria-hidden='true'>
                        {steps.map((item, index) => (
                            <span
                                className={clsx(
                                    'h-2 rounded-full transition-all',
                                    index === currentStepIndex
                                        ? 'w-5 bg-[var(--core-blue)]'
                                        : index < currentStepIndex
                                        ? 'w-2 bg-[color-mix(in_srgb,var(--core-blue)_55%,transparent)]'
                                        : 'w-2 bg-[var(--core-muted-border)]'
                                )}
                                key={item}
                            />
                        ))}
                    </div>
                    <span className='text-xs font-bold uppercase tracking-[0.5px] opacity-55'>
                        {t('order.progress', {
                            current: currentStepIndex + 1,
                            total: steps.length,
                            step: rawT(`order.steps.${step}`),
                            interpolation: { escapeValue: false }
                        })}
                    </span>
                </div>

                {step === 'generate' && (
                    <div className='core-card p-4 sm:p-6'>
                        {data.voices.length > 1 && (
                            <>
                                <p className='mb-3 border-l-[3px] border-[var(--core-yellow)] py-1 pl-3 text-sm font-semibold'>
                                    {t('order.voice-hint')}
                                </p>
                                <div className='mb-5 flex flex-wrap gap-2'>
                                    {data.voices.map((voice, index) => (
                                        <button
                                            className={clsx(
                                                'rounded-[5px] border px-4 py-2 text-[13px] font-semibold transition',
                                                voice.id === activeVoiceId
                                                    ? `${voiceColors[index % voiceColors.length]} border-transparent`
                                                    : 'border-[var(--core-muted-border)] opacity-60 hover:opacity-100'
                                            )}
                                            key={voice.id}
                                            type='button'
                                            onClick={() => onActiveVoiceChange(voice.id)}
                                        >
                                            {voice.name}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        {!activeVoice.voiceSettings
                            ? <p className='text-sm opacity-60'>{t('demo.no-voice-data')}</p>
                            : (
                                <AudioGeneration
                                    hasConsent={data.hasConsent}
                                    isLoggedIn={data.isLoggedIn}
                                    anonymousId={data.anonymousId}
                                    limits={data.limits}
                                    defaultAudioControls={activeVoice.voiceSettings}
                                    generatedTakeName={activeVoice.name}
                                    generatedSpeakerVoiceId={activeVoice.id}
                                    progressBarVariant='unified'
                                    appearance='unified'
                                    theme={theme}
                                    requestDataRefresh={refreshData}
                                >
                                    {parts => (
                                        <div className='grid grid-cols-1 grid-rows-1 overflow-hidden rounded-lg'>
                                            {(parts.consentOverlay.has || parts.limitOverlay.has
                                                || parts.progressBar.has) && (
                                                <div className='z-10 col-start-1 row-start-1'>
                                                    {parts.consentOverlay.render()}
                                                    {parts.limitOverlay.render()}
                                                    {parts.progressBar.has && (
                                                        <div className='core-generation-overlay flex h-full flex-col items-center justify-center gap-3 p-3 backdrop-blur-xs'>
                                                            {parts.progressBar.render()}
                                                            <button
                                                                type='button'
                                                                className='rounded-[5px] border border-[var(--core-text)] bg-[var(--core-bg)] px-4 py-2 text-sm font-semibold text-[var(--core-text)] transition hover:bg-[var(--core-text)] hover:text-[var(--core-bg)] disabled:cursor-wait disabled:opacity-60'
                                                                disabled={parts.isCancelling}
                                                                onClick={parts.onCancel}
                                                            >
                                                                {t('audio-generation:buttons.cancel')}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <div className='z-0 col-start-1 row-start-1'>
                                                {parts.renderForm(
                                                    <>
                                                        {parts.renderTextArea({
                                                            rows: 4,
                                                            className:
                                                                'core-input mb-3 block min-h-[108px] w-full resize-none rounded-lg p-3.5 text-base leading-6 outline-none sm:text-sm'
                                                        })}
                                                        {parts.characterLimitReached.has
                                                            && parts.characterLimitReached.render()}
                                                        {parts.error && (
                                                            <div
                                                                role='alert'
                                                                className='mb-3 flex items-start gap-2.5 rounded-md border border-red-500/50 bg-red-500/10 px-3 py-2.5 text-sm'
                                                            >
                                                                <ExclamationTriangleIcon className='mt-0.5 size-4 shrink-0 text-red-500' />
                                                                <div>
                                                                    <p className='font-semibold'>
                                                                        {t('audio-generation:error-title')}
                                                                    </p>
                                                                    <p className='mt-1 opacity-80'>{parts.error}</p>
                                                                    {parts.blockedAndUnchanged && (
                                                                        <p className='mt-1 opacity-80'>
                                                                            {t('audio-generation:edit-to-continue')}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className='flex flex-wrap items-center gap-2'>
                                                            <button
                                                                className='core-generate-button'
                                                                type='button'
                                                                disabled={parts.isPlayingAudio
                                                                    ? false
                                                                    : !parts.canGenerate}
                                                                onClick={parts.isPlayingAudio
                                                                    ? parts.onStop
                                                                    : parts.onGenerate}
                                                            >
                                                                {parts.isPlayingAudio
                                                                    ? <StopIcon className='size-4' />
                                                                    : <PlayIcon className='size-4' />}
                                                                {parts.isPlayingAudio
                                                                    ? t('audio-generation:buttons.stop')
                                                                    : parts.isGenerating
                                                                    ? t('demo.generating')
                                                                    : t('demo.generate')}
                                                            </button>
                                                        </div>
                                                        <p className='mt-2.5 text-[11px] leading-5 opacity-35'>
                                                            {t('demo.disclaimer')}
                                                        </p>
                                                    </>
                                                )}
                                                {parts.autoplayBlocked.has && (
                                                    <div className='mt-3'>{parts.autoplayBlocked.render()}</div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </AudioGeneration>
                            )}

                        {allTakes.length > 0 && (
                            <div className='mt-5 border-t border-[var(--core-muted-border)] pt-4'>
                                <div className='mb-3 flex items-center justify-between gap-3'>
                                    <h3 className='text-sm font-bold'>{t('order.takes-title')}</h3>
                                    <span className='text-xs opacity-45'>
                                        {t('order.characters', { count: totalCharacters })}
                                    </span>
                                </div>
                                <div className='space-y-4'>
                                    {recordings.map((recording, recordingIndex) => (
                                        <div key={`${recording.text}-${recordingIndex}`}>
                                            {recordings.length > 1 && (
                                                <div className='mb-1 text-[11px] font-bold uppercase tracking-[0.5px] opacity-45'>
                                                    {t('order.recording', { number: recordingIndex + 1 })}
                                                </div>
                                            )}
                                            {recording.takes.map((take, takeIndex) => {
                                                const voice = data.voices.find(voice =>
                                                    voice.id === take.speakerVoiceId
                                                ) ?? data.voices.find(voice => voice.name === take.name);
                                                return (
                                                    <div
                                                        className={clsx(
                                                            'core-take-row',
                                                            recording.takes.length === 1 && 'core-take-row-single'
                                                        )}
                                                        key={take.takeId}
                                                    >
                                                        {recording.takes.length > 1 && (
                                                            <span className='core-take-version text-[10px] font-bold opacity-70'>
                                                                {t('order.version', { number: takeIndex + 1 })}
                                                            </span>
                                                        )}
                                                        <span className='core-take-play'>
                                                            <PlayButton
                                                                audio={take.audio}
                                                                takeId={take.takeId}
                                                                stopTitle={rawT('common:stop')}
                                                                playTitle={rawT('common:play')}
                                                                colorClassName='text-[var(--core-blue)]'
                                                            />
                                                        </span>
                                                        <span className='core-take-content'>
                                                            {data.voices.length > 1 && voice && (
                                                                <span
                                                                    className={clsx(
                                                                        'core-take-voice rounded px-2 py-1 text-[10px] font-bold uppercase',
                                                                        getVoiceColor(voice.id)
                                                                    )}
                                                                    title={voice.name}
                                                                >
                                                                    {voice.name}
                                                                </span>
                                                            )}
                                                            <span className='core-take-text min-w-0 truncate text-[13px] opacity-70'>
                                                                {take.text}
                                                            </span>
                                                        </span>
                                                        <span className='core-take-characters hidden text-[11px] opacity-70 sm:inline'>
                                                            {take.text.length} {t('order.character-short')}
                                                        </span>
                                                        <label className='core-take-buy flex cursor-pointer items-center gap-1 text-[11px] opacity-70'>
                                                            <input
                                                                type='checkbox'
                                                                className='accent-[var(--core-yellow)]'
                                                                checked={take.isChecked}
                                                                onChange={event =>
                                                                    setCheckState(
                                                                        take.takeId,
                                                                        event.target.checked
                                                                    )}
                                                            />
                                                            {t('order.buy-take')}
                                                        </label>
                                                        <button
                                                            type='button'
                                                            className='core-take-delete p-1 opacity-70 transition hover:text-red-400 hover:opacity-100'
                                                            onClick={() => deleteTake(take.takeId)}
                                                            aria-label={rawT('order.delete-take')}
                                                        >
                                                            <TrashIcon className='size-4' />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>

                                <div className='mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--core-muted-border)] pt-4'>
                                    <div className='flex flex-wrap items-center gap-2 text-sm'>
                                        <span>{t('order.generated', { count: allTakes.length })}</span>
                                        <span aria-hidden='true' className='opacity-30'>·</span>
                                        <strong className='text-[var(--core-accent)]'>
                                            {t(hasUseStep ? 'order.price-from' : 'order.price', {
                                                currency: data.currency,
                                                price: selectedPrice.toFixed(2)
                                            })}
                                        </strong>
                                        {data.availableCurrencies.length > 1 && (
                                            <CurrencySwitcher
                                                currency={data.currency}
                                                availableCurrencies={data.availableCurrencies}
                                            />
                                        )}
                                    </div>
                                    <button
                                        className='core-primary-button'
                                        type='button'
                                        disabled={selectedTakes.length === 0 || (!hasUseStep && !selectedUse)}
                                        onClick={() => goToStep(hasUseStep ? 'use' : 'review')}
                                    >
                                        {hasUseStep ? t('order.to-use') : t('order.to-review')}
                                        <ArrowRightIcon className='size-4' />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {step === 'use' && (
                    <div className='core-card core-use-card p-4 sm:p-6'>
                        <div className='mb-4 flex flex-wrap items-start justify-between gap-3'>
                            <div>
                                <h2 className='text-lg font-bold'>{t('usecase.question')}</h2>
                                <p className='mt-1 text-sm opacity-55'>{t('order.use-subtitle')}</p>
                            </div>
                            {data.availableCurrencies.length > 1 && (
                                <CurrencySwitcher
                                    currency={data.currency}
                                    availableCurrencies={data.availableCurrencies}
                                />
                            )}
                        </div>
                        <div className='grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2.5'>
                            {data.availableUses.map(use => (
                                <UseCard
                                    key={use.id}
                                    use={use}
                                    selected={use.id === selectedUseId}
                                    currency={data.currency}
                                    locale={locale}
                                    onSelect={() => onSelectedUseChange(use.id)}
                                />
                            ))}
                        </div>
                        <div className='mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 border-l-[3px] border-[var(--core-yellow)] py-1 pl-3'>
                            <span className='text-[13px] opacity-70'>{t('order.api-note')}</span>
                            <button
                                type='button'
                                className='text-[13px] font-semibold text-[var(--core-blue)] underline decoration-1 underline-offset-4 transition hover:opacity-75'
                                onClick={onApiRequest}
                            >
                                {t('order.api-contact')}
                            </button>
                        </div>
                        <div className='mt-5 flex flex-wrap items-center justify-between gap-3'>
                            <button className='core-back-button' type='button' onClick={() => goToStep('generate')}>
                                <ArrowLeftIcon className='size-4' /> {t('order.back')}
                            </button>
                            <button
                                className='core-primary-button'
                                type='button'
                                disabled={!selectedUse}
                                onClick={() => goToStep('review')}
                            >
                                {t('order.to-review')} <ArrowRightIcon className='size-4' />
                            </button>
                        </div>
                    </div>
                )}

                {step === 'review' && selectedUse && (
                    <CoreCheckout
                        availableUse={selectedUse}
                        currency={data.currency}
                        onBack={() => goToStep(hasUseStep ? 'use' : 'generate')}
                    />
                )}
            </div>
        </section>
    );
}

function UseCard({ use, selected, currency, locale, onSelect }: {
    use: ResolvedAvailableUse;
    selected: boolean;
    currency: string;
    locale: string;
    onSelect: () => void;
}) {
    const { t } = useEditableTranslation('core-shop');
    const { t: tMedium } = useEditableTranslation('medium-selection');

    return (
        <button type='button' className={clsx('core-use-option text-left', selected && 'selected')} onClick={onSelect}>
            <div className='mb-1 text-sm font-bold'>{getMediumTitle(use.medium, locale)}</div>
            <div className='mb-2 text-xs leading-5 opacity-55'>{getMediumExamples(use.medium, locale)}</div>
            <div className='mb-2 text-[11px] font-semibold text-[var(--core-blue)]'>
                {use.timeframeUnit
                    ? tMedium('restricted-usage', {
                        value: use.timeframeValue || 1,
                        unit: use.timeframeUnit
                    })
                    : tMedium('unrestricted-usage')}
            </div>
            <div className='text-sm font-bold text-[var(--core-accent)]'>
                {use.basePrice > 0
                    ? t('usecase.price-from', { currency, price: use.basePrice.toFixed(2) })
                    : t('usecase.free')}
            </div>
        </button>
    );
}
